import { useCallback, useEffect, useRef, useState } from "react";
import {
    Link2, Loader2, RefreshCw, Send, CheckCircle2, AlertTriangle, Clock,
    ShieldCheck, Film, Ban,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { fmtInt, fmtNumber, fmtRiyadhDateTime } from "@/lib/format";
import { OPS } from "@/constants/testIds";

const HR = OPS.hrIntegration;

function Stat({ k, label, value, tone, Icon, hint }) {
    const { lang } = useI18n();
    return (
        <div data-testid={HR.stat(k)}
            className="rounded-2xl border border-[color:var(--border-soft)] surface px-4 py-3">
            <p className="flex items-center gap-1.5 truncate text-xs text-[color:var(--ink-muted)]">
                {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: tone }} /> : null}
                {label}
            </p>
            <p className="kpi-number text-2xl font-extrabold tnum" style={{ color: tone || "var(--ink)" }}>
                <bdi dir="ltr">{value === null || value === undefined ? "—" : fmtInt(value, lang)}</bdi>
            </p>
            {hint ? <p className="mt-0.5 text-[10px] leading-snug text-[color:var(--ink-faint)]">{hint}</p> : null}
        </div>
    );
}

function ClipRow({ label, row, lang, t }) {
    if (!row || !row.count) {
        return (
            <div className="flex items-center justify-between py-1.5 text-xs">
                <span className="text-[color:var(--ink-muted)]">{label}</span>
                <span className="text-[color:var(--ink-faint)]">{t("hr_ev_no_files")}</span>
            </div>
        );
    }
    return (
        <div className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-xs">
            <span className="font-semibold text-[color:var(--ink)]">{label}</span>
            <span className="tnum text-[color:var(--ink-muted)]">
                <bdi dir="ltr">
                    {fmtInt(row.count, lang)} · {t("hr_ev_median")} {fmtNumber(row.median_mb ?? 0, lang, 1)} MB
                    {" · "}p90 {fmtNumber(row.p90_mb ?? 0, lang, 1)} MB
                    {" · "}{t("hr_ev_max")} {fmtNumber(row.max_mb ?? 0, lang, 1)} MB
                    {" · "}{t("hr_ev_cap")} {fmtInt(row.cap_mb ?? 0, lang)} MB
                </bdi>
            </span>
            {row.over_cap ? (
                <Badge variant="warning" data-testid={HR.over25(label)}>
                    <bdi dir="ltr">{fmtInt(row.over_cap, lang)}</bdi> {t("hr_ev_over_cap")}
                </Badge>
            ) : null}
        </div>
    );
}

/**
 * Admin → HR integration.
 *
 * The HR platform cannot open our `/api/files/...` links (their managers have
 * no Ops login), so we push the evidence bytes to them. What HR actually holds
 * is READ FROM HR — never from our own bookkeeping — because the two would
 * drift the first time an upload was lost.
 */
export default function HrIntegrationPage() {
    const { t, lang } = useI18n();
    const [data, setData] = useState(null);
    const [health, setHealth] = useState(null);
    const [busy, setBusy] = useState(null);   // "send" | "check" | null
    const [confirmSend, setConfirmSend] = useState(false);
    // «تحقّق» re-queues every violation HR lacks and re-arms its missing photos — asked first
    const [confirmCheck, setConfirmCheck] = useState(false);

    const load = useCallback(async () => {
        try {
            const [ev, h] = await Promise.all([
                api.get("/integration/hr-evidence"),
                api.get("/integration/hr-health"),
            ]);
            setData(ev.data);
            setHealth(h.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
            setData({});
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    // The server only queues and wakes the background drainer, so look again a
    // little later to show the files leaving the queue.
    const later = useRef(null);
    useEffect(() => {
        const timer = later;
        return () => clearTimeout(timer.current);
    }, []);

    const run = async (kind) => {
        setConfirmSend(false);
        setConfirmCheck(false);
        if (busy) return;
        setBusy(kind);
        try {
            if (kind === "send") {
                const r = await api.post("/integration/hr-evidence/send-now");
                toast.success(t("hr_ev_sent_toast").replace("{n}", fmtInt(r.data.files_queued ?? 0, lang)));
                clearTimeout(later.current);
                later.current = setTimeout(load, 15000);
            } else {
                const r = await api.post("/integration/hr-evidence/reconcile");
                if (r.data.error) toast.error(r.data.error);
                else toast.success(t("hr_ev_checked_toast")
                    .replace("{n}", r.data.checked)
                    .replace("{w}", r.data.waiting + r.data.unknown));
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusy(null);
        }
    };

    const files = data?.files || {};
    const truth = data?.hr_truth || {};
    const blocked = (files.rejected || 0) + (files.too_large || 0) + (files.missing || 0) + (files.over_limit || 0);

    return (
        <div data-testid={HR.page} className="space-y-6">
            <PageHeader
                icon={Link2}
                title={t("hr_ev_title")}
                subtitle={t("hr_ev_subtitle")}
                primaryAction={
                    <Button data-testid={HR.sendNow} onClick={() => setConfirmSend(true)}
                        disabled={!!busy} className="gap-2">
                        {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {t("hr_ev_send_now")}
                    </Button>
                }
                actions={
                    <Button data-testid={HR.reconcile} variant="outline" onClick={() => setConfirmCheck(true)}
                        disabled={!!busy} className="gap-2">
                        {busy === "check" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        {t("hr_ev_check_with_hr")}
                    </Button>
                }
            />

            {!data ? (
                <Skeleton className="h-40 w-full" />
            ) : (
                <>
                    {!data.configured ? (
                        <Card data-testid={HR.notConfigured}
                            className="surface border-[color:var(--warning)] rounded-2xl">
                            <CardContent className="flex items-start gap-2 py-4 text-sm">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning)]" />
                                <span className="text-[color:var(--ink-muted)]">{t("hr_ev_not_configured")}</span>
                            </CardContent>
                        </Card>
                    ) : null}

                    {/* What HR says it holds — their truth, not ours */}
                    <div>
                        <h2 className="mb-2 flex items-center gap-2 text-base font-bold text-[color:var(--ink)]">
                            <ShieldCheck className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            {t("hr_ev_truth_title")}
                        </h2>
                        <p className="mb-3 text-xs text-[color:var(--ink-muted)]">{t("hr_ev_truth_desc")}</p>
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <Stat k="held" Icon={CheckCircle2} tone="var(--success)"
                                label={t("hr_ev_held")} value={truth.evidence_held} />
                            <Stat k="waiting" Icon={Clock} tone="#B45309"
                                label={t("hr_ev_waiting")} value={truth.waiting} />
                            <Stat k="unknown" Icon={AlertTriangle} tone="var(--danger)"
                                label={t("hr_ev_unknown")} value={truth.unknown}
                                hint={t("hr_ev_unknown_hint")} />
                            <Stat k="unchecked" label={t("hr_ev_never_checked")}
                                value={truth.never_checked} hint={t("hr_ev_never_checked_hint")} />
                        </div>
                        <p className="mt-2 text-[10px] text-[color:var(--ink-faint)]">
                            {t("hr_ev_last_checked")}:{" "}
                            <bdi data-testid={HR.lastChecked} title={data.last_reconciled_at || undefined}>
                                {fmtRiyadhDateTime(data.last_reconciled_at, lang)}
                            </bdi>
                            {" · "}
                            {t("hr_ev_violations_sent")}:{" "}
                            <bdi dir="ltr">{fmtInt(data.violations_sent_to_hr || 0, lang)}</bdi>
                        </p>
                    </div>

                    {/* Our delivery queue */}
                    <div>
                        <h2 className="mb-2 text-base font-bold text-[color:var(--ink)]">{t("hr_ev_queue_title")}</h2>
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <Stat k="stored" Icon={CheckCircle2} tone="var(--success)"
                                label={t("hr_ev_delivered")}
                                value={(files.stored || 0) + (files.duplicate || 0)} />
                            <Stat k="pending" Icon={Clock} label={t("hr_ev_in_queue")} value={files.pending} />
                            <Stat k="failed" Icon={AlertTriangle} tone="#B45309"
                                label={t("hr_ev_retrying")} value={files.failed} />
                            <Stat k="blocked" Icon={Ban} tone="var(--danger)"
                                label={t("hr_ev_blocked")} value={blocked}
                                hint={t("hr_ev_blocked_hint")} />
                        </div>
                    </div>

                    {(data.recent_errors || []).length ? (
                        <Card className="surface border-[color:var(--border-soft)] rounded-2xl">
                            <CardContent className="py-4">
                                <p className="mb-2 text-sm font-bold text-[color:var(--ink)]">{t("hr_ev_errors_title")}</p>
                                <div className="space-y-2" data-testid={HR.errors}>
                                    {data.recent_errors.map((e, i) => (
                                        <div key={i} className="rounded-lg bg-[color:var(--bg-surface-2)] px-3 py-2 text-[11px]">
                                            <span className="font-semibold text-[color:var(--ink)]">
                                                <bdi>{e.file_name}</bdi>
                                            </span>
                                            <Badge variant="secondary" className="mx-2 text-[10px]">{e.status}</Badge>
                                            <span className="text-[color:var(--ink-muted)]"><bdi>{e.last_error}</bdi></span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    ) : null}

                    {/* CCTV clip sizes — the numbers HR asked for to set their cap */}
                    <Card className="surface border-[color:var(--border-soft)] rounded-2xl">
                        <CardContent className="py-4">
                            <p className="mb-1 flex items-center gap-2 text-sm font-bold text-[color:var(--ink)]">
                                <Film className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                {t("hr_ev_clips_title")}
                            </p>
                            <p className="mb-2 text-xs text-[color:var(--ink-muted)]">{t("hr_ev_clips_desc")}</p>
                            <div data-testid={HR.clips} className="divide-y divide-[color:var(--separator)]">
                                <ClipRow label={t("hr_ev_clips_video")} row={data.cctv_clip_sizes?.video} lang={lang} t={t} />
                                <ClipRow label={t("hr_ev_clips_photo")} row={data.cctv_clip_sizes?.photo} lang={lang} t={t} />
                            </div>
                            <p className="mt-2 text-[10px] text-[color:var(--ink-faint)]">
                                {t("hr_ev_limits")
                                    .replace("{files}", data.limits?.max_files_per_violation ?? 10)
                                    .replace("{mb}", data.limits?.max_file_mb ?? 25)
                                    .replace("{vmb}", data.limits?.max_video_mb ?? 150)}
                            </p>
                        </CardContent>
                    </Card>

                    {health ? (
                        <p data-testid={HR.health} className="text-[11px] text-[color:var(--ink-muted)]">
                            {t("hr_ev_connection")}:{" "}
                            {health.reachable ? (
                                <span className="font-semibold text-[color:var(--success)]">{t("hr_ev_online")}</span>
                            ) : (
                                <span className="font-semibold text-[color:var(--danger)]">
                                    {t("hr_ev_offline")}{health.error ? ` — ${health.error}` : ""}
                                </span>
                            )}
                        </p>
                    ) : null}
                </>
            )}

            <AlertDialog open={confirmCheck} onOpenChange={setConfirmCheck}>
                <AlertDialogContent data-testid={HR.reconcileConfirm}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("hr_ev_check_confirm_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("hr_ev_check_confirm_body")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={HR.reconcileConfirmStart} onClick={() => run("check")}>
                            {t("hr_ev_check_with_hr")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
                <AlertDialogContent data-testid={HR.sendConfirm}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("hr_ev_send_confirm_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("hr_ev_send_confirm_body")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={HR.sendConfirmStart} onClick={() => run("send")}>
                            {t("hr_ev_send_now")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
